// Public destination guide page (programmatic SEO target). Mounted at
// /guide/:slug — hydrates a Destination + its itinerary days from the backend
// and renders a hero, top activities, a map, and a CTA into the AI planner.
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { B } from '../constants';
import { guides } from '../api';
import MapView from '../components/MapView';
import DestinationPhotoGallery from '../components/DestinationPhotoGallery';
import DestinationFactStrip from '../components/DestinationFactStrip';
import RelatedDestinations from '../components/RelatedDestinations';

// Sections rendered as plain prose. The model is instructed to emit markdown
// but we treat the body as text and split on blank lines — safer than
// dropping a full markdown renderer in for SEO-only pages.
function renderProse(body) {
  if (!body) return null;
  return String(body)
    .split(/\n{2,}/)
    .map((p, i) => (
      <p key={i} style={{ marginBottom: 12, lineHeight: 1.7, color: B.night }}>{p.trim()}</p>
    ));
}

const LONG_FORM_SECTIONS = [
  { key: 'intro',          title: 'About this destination' },
  { key: 'whenToVisit',    title: 'When to visit' },
  { key: 'gettingThere',   title: 'Getting there' },
  { key: 'gettingAround',  title: 'Getting around' },
  { key: 'whereToStay',    title: 'Where to stay' },
  { key: 'foodAndDrink',   title: 'Food & drink' },
  { key: 'topThingsToDo',  title: 'Top things to do' },
  { key: 'daytrips',       title: 'Day trips' },
  { key: 'safetyTips',     title: 'Safety tips' },
  { key: 'cultureTips',    title: 'Culture tips' },
];

export default function GuideScreen() {
  const { slug } = useParams();
  const [destination, setDestination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    guides
      .get(slug)
      .then((d) => { if (!cancelled) setDestination(d.guide || d); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Guide not found'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  // Set SEO meta tags + JSON-LD schema.org/TouristDestination so search
  // engines can render rich results for the guide.
  useEffect(() => {
    if (destination) {
      document.title = `${destination.name} Travel Guide 2026 | GreatBreak`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', destination.description?.slice(0, 160) || `Plan your trip to ${destination.name} with our free AI itinerary generator.`);
      }
    }
    const tagId = 'guide-jsonld';
    const existing = document.getElementById(tagId);
    if (existing) existing.remove();
    if (destination) {
      const jsonld = {
        '@context': 'https://schema.org',
        '@type': 'TouristDestination',
        name: destination.name,
        description: destination.description,
        image: destination.img,
        touristType: destination.vibe,
      };
      if (destination.content && Array.isArray(destination.content.faqsJson) && destination.content.faqsJson.length > 0) {
        jsonld.subjectOf = {
          '@type': 'FAQPage',
          mainEntity: destination.content.faqsJson.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        };
      }
      const script = document.createElement('script');
      script.id = tagId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify(jsonld);
      document.head.appendChild(script);
    }
    return () => {
      document.title = 'GreatBreak — AI Travel Planner';
      const tag = document.getElementById(tagId);
      if (tag) tag.remove();
    };
  }, [destination]);

  // Map locations: each activity that has a placeId/lat/lng gets a pin. The
  // schema only stores name + placeId, so without explicit coords we just skip
  // those activities. The map component is still mounted so the page layout is
  // stable even when there are no markers.
  const mapLocations = useMemo(() => {
    if (!destination?.itineraryDays) return [];
    const out = [];
    destination.itineraryDays.forEach((day) => {
      (day.activities || []).forEach((act, idx) => {
        if (act.lat && act.lng) {
          out.push({
            lat: act.lat,
            lng: act.lng,
            title: act.name,
            description: '',
            dayNumber: day.dayNumber,
            activityIndex: idx,
          });
        }
      });
    });
    return out;
  }, [destination]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: B.sandPattern }}>
        <p style={{ color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>Loading guide…</p>
      </div>
    );
  }

  if (error || !destination) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: B.sandPattern, padding: 40, textAlign: 'center' }}>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 32, marginBottom: 12, color: B.night }}>Guide not found</h1>
        <p style={{ color: B.muted, marginBottom: 20 }}>{error || `We couldn't find a guide for "${slug}".`}</p>
        <a href="/" style={{ background: B.forest, color: 'white', padding: '12px 24px', borderRadius: 24, textDecoration: 'none', fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>
          ← Back to GreatBreak
        </a>
      </div>
    );
  }

  const planHref = `/?screen=ai&destination=${encodeURIComponent(destination.name)}`;
  const topActivities = (destination.itineraryDays || [])
    .flatMap((d) => (d.activities || []).map((a) => ({ ...a, dayNumber: d.dayNumber })))
    .slice(0, 12);

  return (
    <div style={{ background: B.sandPattern, minHeight: '100vh' }}>
      {/* Hero */}
      <div
        style={{
          background: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(${destination.img}) center/cover`,
          padding: '80px 40px',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <p style={{ fontFamily: "'Montserrat',sans-serif", letterSpacing: 2, fontSize: 12, fontWeight: 700, marginBottom: 12, opacity: 0.9 }}>
          GREATBREAK GUIDE
        </p>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 52, fontWeight: 700, marginBottom: 16 }}>
          {destination.name}
        </h1>
        <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 16, maxWidth: 720, margin: '0 auto', lineHeight: 1.7, opacity: 0.95 }}>
          {destination.description}
        </p>
        <a
          href={planHref}
          style={{
            display: 'inline-block',
            marginTop: 28,
            background: B.amber,
            color: B.night,
            padding: '14px 28px',
            borderRadius: 30,
            textDecoration: 'none',
            fontFamily: "'Montserrat',sans-serif",
            fontSize: 14,
            fontWeight: 800,
            boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
          }}
        >
          ✈️ Plan your trip to {destination.name} with AI
        </a>
      </div>

      {/* Quick facts */}
      <div style={{ display: 'flex', gap: 12, padding: '28px 40px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {[
          { label: 'Duration', value: `${destination.days} days` },
          { label: 'Budget', value: `$${destination.budget}` },
          { label: 'Vibe', value: destination.vibe },
          { label: 'Best season', value: destination.bestSeason },
          { label: 'Climate', value: destination.climate },
          { label: 'Flight time', value: destination.flightTime },
        ].map((s) => (
          <div key={s.label} style={{ background: 'white', borderRadius: 12, padding: '12px 18px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', minWidth: 140 }}>
            <div style={{ color: B.muted, fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', fontFamily: "'Montserrat',sans-serif" }}>
              {s.label}
            </div>
            <div style={{ color: B.night, fontSize: 14, fontWeight: 700, fontFamily: "'Montserrat',sans-serif" }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Top activities + map */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 24, padding: '0 40px 40px', maxWidth: 1280, margin: '0 auto' }}>
        <div>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, marginBottom: 16, color: B.night }}>
            Top experiences
          </h2>
          {topActivities.length === 0 ? (
            <p style={{ color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>
              We're still curating activities for this guide.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {topActivities.map((act) => (
                <div key={act.id} style={{ background: 'white', borderRadius: 12, padding: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  <div style={{ color: B.forest, fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 4, fontFamily: "'Montserrat',sans-serif" }}>
                    Day {act.dayNumber}
                  </div>
                  <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 600, color: B.night }}>
                    {act.name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, marginBottom: 16, color: B.night }}>
            On the map
          </h2>
          <div style={{ borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
            <MapView locations={mapLocations} height={400} />
          </div>
        </div>
      </div>

      {/* Rich content sections */}
      {destination.content && (
        <div style={{ maxWidth: 920, margin: '0 auto', padding: '0 40px 16px' }}>
          <DestinationFactStrip content={destination.content} />
          {LONG_FORM_SECTIONS.map((s) =>
            destination.content[s.key] ? (
              <section key={s.key} style={{ margin: '24px 0' }}>
                <h2 style={{
                  fontFamily: "'Playfair Display',serif", color: B.night,
                  fontSize: 24, marginBottom: 12,
                }}>
                  {s.title}
                </h2>
                <div style={{ fontFamily: "'Montserrat',sans-serif" }}>
                  {renderProse(destination.content[s.key])}
                </div>
              </section>
            ) : null,
          )}
        </div>
      )}

      {/* Photo gallery */}
      {destination.photos && destination.photos.length > 0 && (
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 40px' }}>
          <DestinationPhotoGallery photos={destination.photos} />
        </div>
      )}

      {/* FAQ accordion */}
      {destination.content && Array.isArray(destination.content.faqsJson) && destination.content.faqsJson.length > 0 && (
        <div style={{ maxWidth: 920, margin: '0 auto', padding: '0 40px 16px' }}>
          <h2 style={{
            fontFamily: "'Playfair Display',serif", color: B.night,
            fontSize: 24, marginBottom: 12,
          }}>
            Frequently asked questions
          </h2>
          {destination.content.faqsJson.map((f, idx) => (
            <details
              key={idx}
              style={{
                background: '#fff', borderRadius: 12, marginBottom: 8,
                border: `1px solid ${B.sand}`, padding: '12px 16px',
              }}
            >
              <summary style={{
                cursor: 'pointer', fontWeight: 600, color: B.night,
                fontFamily: "'Montserrat',sans-serif",
              }}>
                {f.q}
              </summary>
              <div style={{
                marginTop: 8, color: B.night, lineHeight: 1.6,
                fontFamily: "'Montserrat',sans-serif", fontSize: 14,
              }}>
                {f.a}
              </div>
            </details>
          ))}
        </div>
      )}

      {/* Related destinations */}
      {destination.relatedDestinations && destination.relatedDestinations.length > 0 && (
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 40px' }}>
          <RelatedDestinations items={destination.relatedDestinations} />
        </div>
      )}

      {/* CTA footer */}
      <div style={{ background: 'white', padding: '48px 24px', textAlign: 'center', borderTop: '1px solid #e5e0d8' }}>
        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, marginBottom: 8, color: B.night }}>
          Ready to go to {destination.name}?
        </h2>
        <p style={{ color: B.muted, fontFamily: "'Montserrat',sans-serif", marginBottom: 20 }}>
          Generate a personalized day-by-day itinerary in seconds.
        </p>
        <a
          href={planHref}
          style={{
            display: 'inline-block',
            background: B.forest,
            color: 'white',
            padding: '14px 28px',
            borderRadius: 30,
            textDecoration: 'none',
            fontFamily: "'Montserrat',sans-serif",
            fontSize: 14,
            fontWeight: 800,
            boxShadow: '0 6px 20px rgba(26,107,74,0.35)',
          }}
        >
          Plan your trip with AI →
        </a>
      </div>
    </div>
  );
}
